import i18next from 'i18next';
import { makeAutoObservable } from 'mobx';
import { CacheAreaKey } from 'core/config/cache-config';
import { PaymentResponseStatus, PaymentStatusGroup } from 'core/enums/payment-status';
import { PaymentStatusDTO } from 'core/models/account/make-payment-payload.dto';
import { StorageHelper } from 'core/services/storage';
import { PaymentSession } from 'payment/payment.context';
import { MakePaymentPayloadDTO } from './interfaces/make-payment-payload.dto';
import { MomentGetPaymentStatusResponse, MomentMakePaymentRequest } from './interfaces/moment-make-payment';
import { PaymentService } from './interfaces/payment-service';
import { RequestRapydCheckout } from './interfaces/request-rapyd-checkout';
import { PaymentStatus } from './payment.store';

export const PaymentProcessingLimits = {
  MaxTime: 60000,
  MaxErrors: 5,
  PollTimeInterval: 2000
}

export const PollingDefaults = {
  pollCount: 0,
  duration: 10000,
  errorCount: 0
}

export type PaymentResponseMessage = { heading?: string, message: string }

const isMomentError = (statusResponse: PaymentStatusDTO | MomentGetPaymentStatusResponse): statusResponse is MomentGetPaymentStatusResponse => {
  return 'last_payment_error' in (statusResponse as MomentGetPaymentStatusResponse);
};

const lang = (key: string) => i18next.t(`payment.paymentResponseMessage.${key}`)

export class PaymentPollingStore {
  pollingStats = PollingDefaults;
  pendingPaymentPoll!: NodeJS.Timer;
  status = PaymentStatus.None;
  message!: PaymentResponseMessage | null;
  countryName!: string;
  customerNumber!: string;

  isPolling = false;

  public static transactionIds: Array<string> = [];

  private paymentMessagePointer: { [key: PaymentResponseStatus | string]: PaymentResponseMessage } = {
    [PaymentResponseStatus.PAYMENT_SUCCESS]: { message: lang('paymentSuccess') },
    [PaymentResponseStatus.PSO_COMPLETED]: { message: lang('psoCompleted') },
    [PaymentResponseStatus.PSO_RETRYING]: { message: lang('psoRetrying') },
    [PaymentResponseStatus.PSO_PROCESSING]: { message: lang('psoProcessing') },
    ['TIMEOUT']: {
      message: lang('timeout'),
      heading: lang('timeoutHeading')
    },
  }

  private actions = {
    onCreated: () => {
      this.changeStatusAndMessage(PaymentStatus.Created);
      this.onPaymentConclusion();
    },
    onPending: (message?: PaymentResponseMessage) => {
      this.changeStatusAndMessage(PaymentStatus.Pending, message);
    },
    onSuccess: (message?: PaymentResponseMessage) => {
      this.changeStatusAndMessage(PaymentStatus.Success, message);
      // Note: Always debounce account cache on payment- it will always update.
      StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Account)
      this.onPaymentConclusion();
    },
    onFailed: (message: PaymentResponseMessage) => {
      this.changeStatusAndMessage(PaymentStatus.Failure, message);
      this.onPaymentConclusion();
    },
    onTimeout: (message: PaymentResponseMessage) => {
      this.changeStatusAndMessage(PaymentStatus.Timeout, message);
      this.onPaymentConclusion();
    },
  }

  private transaction!: {
    id: string;
  } | null;

  constructor(private paymentService: PaymentService<MakePaymentPayloadDTO | RequestRapydCheckout | MomentMakePaymentRequest>, countryName: string, customerNumber: string) {
    makeAutoObservable(this);
    this.changeCountryName(countryName);
    this.changeCustomerNumber(customerNumber);

    const paymentSession = StorageHelper.GetInstance.getFromCache(
      'sessions',
      'create',
      CacheAreaKey.Payments
    ) as PaymentSession;

    if (paymentSession) {
      this.changeStatusAndMessage(paymentSession.status, paymentSession.message, false);
    }
  }

  changeCustomerNumber(customerNumber: string) {
    this.customerNumber = customerNumber;
  }

  changeCountryName(countryName: string) {
    this.countryName = countryName;
  }

  startPoll(transactionId: string) {
    if (!this.paymentService.canStatusPoll) {
      this.clearPoll();
      this.actions.onSuccess();
      return;
    }

    if (this.isPolling || PaymentPollingStore.transactionIds.includes(transactionId)) {
      return;
    }

    if (this.pendingPaymentPoll) {
      this.clearPoll();
    }

    this.status = PaymentStatus.Pending;
    this.message = null;
    this.transaction = { id: transactionId };
    this.isPolling = true;

    this.checkStatus();

    PaymentPollingStore.transactionIds.push(transactionId);

    this.pendingPaymentPoll = setInterval(() => {
      this.pollingStats.pollCount += 1;
      this.checkStatus();
    }, PaymentProcessingLimits.PollTimeInterval);
  }

  clearPoll() {
    this.pollingStats = PollingDefaults;
    this.transaction = null;
    this.isPolling = false;

    if (this.pendingPaymentPoll) {
      clearInterval(this.pendingPaymentPoll);
    }
  }

  clearState() {
    this.clearPoll();
    this.status = PaymentStatus.None;
    this.message = null;
  }

  paymentFailed(reason: string) {
    this.actions.onFailed({ message: reason });
  }

  // NOTE: this should only be used in edge-cases where polling should be bypassed
  paymentSuccess() {
    this.actions.onSuccess();
  }

  private onPaymentConclusion() {
    this.clearPoll();
  }

  private changeStatusAndMessage(status: PaymentStatus, message?: PaymentResponseMessage, updateCache = true) {
    const paymentSession = StorageHelper.GetInstance.getFromCache('sessions', 'create', CacheAreaKey.Payments) || {} as any;

    if (message) {
      paymentSession.message = message;
      this.message = message;
    }

    paymentSession.status = status;
    this.status = status;

    if (!updateCache) {
      return;
    }

    StorageHelper.GetInstance.setCache('sessions', paymentSession, CacheAreaKey.Payments, 'create')
  }

  private checkStatus() {
    if (!this.transaction) {
      console.error('Transaction lost')
      this.paymentFailed('Transaction lost');
      return;
    }

    this.paymentService.PaymentStatus(this.countryName, this.customerNumber, this.transaction.id)
      .then(
        (statusResponse: PaymentStatusDTO | MomentGetPaymentStatusResponse) => {
          this.pollingStats.errorCount = 0;

          const status = `${statusResponse.status}`.toUpperCase() as PaymentResponseStatus;

          const isPending = PaymentStatusGroup.pending.includes(status);
          const isFailure = PaymentStatusGroup.failed.includes(status);
          const isSuccessful = PaymentStatusGroup.success.includes(status);
          const isCreated = PaymentStatusGroup.notProcessing.includes(status);
          const statusMessage = this.getStatusMessage(status);

          if (isFailure) {
            const failedMessage = isMomentError(statusResponse) && `${statusResponse.last_payment_error?.errorMessage}`;

            this.actions.onFailed({ message: failedMessage || '' });
            return;
          }

          if (isSuccessful) {
            this.actions.onSuccess(statusMessage);
            return;
          }

          // TODO: re-add this once moment issue is resolved.
          // if (isCreated) {
          //   this.actions.onCreated();
          //   return;
          // }

          const pollTime = this.pollingStats.pollCount * PaymentProcessingLimits.PollTimeInterval;

          if (this.paymentService.canTimeoutPoll && pollTime >= PaymentProcessingLimits.MaxTime) {
            // NOTE: Due to limitations on Moment the Created status should be treated as a payment timeout failure
            // TODO: remove this once moment issue is resolved.
            if (isCreated) {
              this.actions.onFailed({ message: lang('paymentFailed') });
              return;
            }

            const timeoutMessage = this.getStatusMessage('TIMEOUT') || 'Timeout';

            this.actions.onTimeout(timeoutMessage);
            return;
          }

          if (isPending) {
            this.actions.onPending(statusMessage);
            return;
          }

          this.handlePollingError();
        },
        (error) => {
          console.error('Error Checking Status -> ', error);
          this.handlePollingError();
        }
      );
  }

  private getStatusMessage(status: string) {
    const message = this.paymentMessagePointer[status];

    return message || null;
  }

  private handlePollingError() {
    this.pollingStats.errorCount += 1;
    if (this.pollingStats.errorCount >= PaymentProcessingLimits.MaxErrors) {
      this.paymentFailed('Max error count reached on poll');
    }
  }
}